import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CommissionFilters from './components/CommissionFilters';
import CommissionSummary from './components/CommissionSummary';
import CommissionTable from './components/CommissionTable';
import PaymentMethods from './components/PaymentMethods';
import DisputeModal from './components/DisputeModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CommissionTracking = () => {
  const [activeTab, setActiveTab] = useState('transactions');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    type: 'all',
    venue: 'all',
    minAmount: '',
    maxAmount: ''
  });
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Mock data for commission summary
  const summaryData = {
    totalEarned: 15420.50,
    pendingCommissions: 3250.75,
    nextPaymentDate: '15/01/2025',
    minimumThreshold: 500.00,
    thresholdProgress: '85% atteint'
  };

  // Mock data for transactions
  const [transactions] = useState([
    {
      id: 'TXN-001',
      date: '2024-12-28',
      venue: 'Club Paradiso',
      artist: 'DJ Martin Solveig',
      eventType: 'Soirée électro',
      commission: 450.00,
      commissionRate: 15,
      bookingValue: 3000.00,
      status: 'paid',
      attributionModel: 'Dernier clic',
      attributionWindow: '30 jours',
      processedDate: '2025-01-02',
      paymentMethod: 'Virement bancaire',
      transactionId: 'PAY-2025-001'
    },
    {
      id: 'TXN-002',
      date: '2024-12-25',
      venue: 'Festival Electrobeach',
      artist: 'Charlotte de Witte',
      eventType: 'Festival techno',
      commission: 1200.00,
      commissionRate: 20,
      bookingValue: 6000.00,
      status: 'pending',
      attributionModel: 'Premier clic',
      attributionWindow: '45 jours',
      processedDate: null,
      paymentMethod: 'Virement bancaire',
      transactionId: 'PAY-2025-002'
    },
    {
      id: 'TXN-003',
      date: '2024-12-20',
      venue: 'Le Rex Club',
      artist: 'Amelie Lens',
      eventType: 'Soirée underground',
      commission: 320.50,
      commissionRate: 12,
      bookingValue: 2670.83,
      status: 'processing',
      attributionModel: 'Linéaire',
      attributionWindow: '30 jours',
      processedDate: null,
      paymentMethod: 'PayPal',
      transactionId: 'PAY-2025-003'
    },
    {
      id: 'TXN-004',
      date: '2024-12-18',
      venue: 'Warehouse Paris',
      artist: 'Boris Brejcha',
      eventType: 'Soirée progressive',
      commission: 680.25,
      commissionRate: 18,
      bookingValue: 3779.17,
      status: 'disputed',
      attributionModel: 'Dernier clic',
      attributionWindow: '30 jours',
      processedDate: null,
      paymentMethod: 'Virement bancaire',
      transactionId: 'PAY-2025-004'
    },
    {
      id: 'TXN-005',
      date: '2024-12-15',
      venue: 'Café de la Danse',
      artist: 'Maceo Plex',
      eventType: 'Concert live',
      commission: 275.00,
      commissionRate: 10,
      bookingValue: 2750.00,
      status: 'paid',
      attributionModel: 'Premier clic',
      attributionWindow: '60 jours',
      processedDate: '2024-12-20',
      paymentMethod: 'Virement bancaire',
      transactionId: 'PAY-2024-156'
    }
  ]);

  // Mock data for payment methods
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'bank',
      bankName: 'BNP Paribas',
      iban: 'FR1420041010050500013M02606',
      bic: 'BNPAFRPP',
      accountHolder: 'Jean Dupont',
      isDefault: true
    },
    {
      id: 2,
      type: 'paypal',
      paypalEmail: 'jean.dupont@email.com',
      isDefault: false
    }
  ]);

  const tabs = [
    { id: 'transactions', label: 'Transactions', icon: 'Receipt' },
    { id: 'payment-methods', label: 'Méthodes de paiement', icon: 'CreditCard' }
  ];

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      status: 'all',
      type: 'all',
      venue: 'all',
      minAmount: '',
      maxAmount: ''
    });
  };

  const handleDisputeTransaction = (transactionId) => {
    const transaction = transactions?.find(t => t?.id === transactionId);
    setSelectedTransaction(transaction);
    setIsDisputeModalOpen(true);
  };

  const handleSubmitDispute = (transactionId, disputeData) => {
    console.log('Dispute submitted for transaction:', transactionId, disputeData);
    // Here you would typically send the dispute to your backend
  };

  const handleAddPaymentMethod = (methodData) => {
    const newMethod = {
      id: paymentMethods?.length + 1,
      ...methodData
    };
    setPaymentMethods(prev => [...prev, newMethod]);
  };

  const handleUpdatePaymentMethod = (methodId, methodData) => {
    setPaymentMethods(prev => 
      prev?.map(method => 
        method?.id === methodId ? { ...method, ...methodData } : method
      )
    );
  };

  const handleDeletePaymentMethod = (methodId) => {
    setPaymentMethods(prev => prev?.filter(method => method?.id !== methodId));
  };

  const exportTransactions = () => {
    console.log('Exporting transactions...');
    // Here you would implement the export functionality
  };

  return (
    <>
      <Helmet>
        <title>Suivi des Commissions - Artis.io</title>
        <meta name="description" content="Suivez vos commissions, gérez vos paiements et consultez l'historique de vos transactions sur Artis.io" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Suivi des Commissions</h1>
                <p className="text-muted-foreground mt-2">
                  Gérez vos revenus et suivez vos paiements en temps réel
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  onClick={exportTransactions}
                  iconName="Download"
                  iconPosition="left"
                >
                  Exporter
                </Button>
                <Button
                  variant="default"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <CommissionSummary summary={summaryData} />

            {/* Tab Navigation */}
            <div className="border-b border-border mb-6">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                      activeTab === tab?.id
                        ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'transactions' && (
              <div className="space-y-6">
                <CommissionFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onReset={handleResetFilters}
                />
                <CommissionTable
                  transactions={transactions}
                  onDisputeTransaction={handleDisputeTransaction}
                />
              </div>
            )}

            {activeTab === 'payment-methods' && (
              <PaymentMethods
                paymentMethods={paymentMethods}
                onAddMethod={handleAddPaymentMethod}
                onUpdateMethod={handleUpdatePaymentMethod}
                onDeleteMethod={handleDeletePaymentMethod}
              />
            )}
          </div>
        </main>

        {/* Dispute Modal */}
        <DisputeModal
          isOpen={isDisputeModalOpen}
          onClose={() => setIsDisputeModalOpen(false)}
          transaction={selectedTransaction}
          onSubmitDispute={handleSubmitDispute}
        />
      </div>
    </>
  );
};

export default CommissionTracking;